<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminBatchInquiryList" label="Batch Reports" />
      <breadcrumb-link view="AdminBatchInquiryReport" label="Batch Report" :active="true" />
    </template>

    <div class="box">
      <div class="level">
        <div class="level-left">
          <div>
            <h1 class="title">Batch Report #{{ batchId }}</h1>
            <span class="tag" :class="statusClass">{{ batch ? batch.status : '...' }}</span>
          </div>
        </div>

        <div class="level-right" v-if="batch">
          <b-button
            v-if="canClose"
            type="is-danger"
            icon-left="close"
            class="mr-2"
            :loading="recovering"
            @click="closeBatchInquiry"
          >
            Close batch
          </b-button>
          <b-button
            type="is-light"
            icon-left="download"
            class="mr-2"
            :disabled="inquiries.length === 0"
            @click="exportBatchInquiry"
          >
            Export
          </b-button>
          <b-dropdown position="is-bottom-left" aria-role="menu">
            <template #trigger>
              <b-button type="is-info" icon-right="menu-down">Summary</b-button>
            </template>
            <b-dropdown-item custom>
              <p><strong>Ran:</strong> {{ ranCount }} of {{ batch.total }}</p>
              <p><strong>Inactive:</strong> {{ inactiveCount }}</p>
              <p><strong>Unavailable:</strong> {{ unavailableCount }}</p>
              <p v-if="failedCount > 0"><strong>Failed:</strong> {{ failedCount }}</p>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div v-if="batch && batch.user" class="mt-2">
        <p class="heading">Run By</p>
        <p>
          {{ batch.user.first_name }} {{ batch.user.last_name }}
          ({{ batch.user.email }})
          <span v-if="batch.organization"> — {{ batch.organization.name }}</span>
        </p>
      </div>

      <div v-if="batch" class="mt-4">
        <p class="heading">Progress: {{ batch.completed }} of {{ batch.total }}</p>
        <b-progress
          :value="batch.completed"
          :max="batch.total"
          type="is-info"
          show-value
          format="percent"
        />
      </div>

      <hr />

      <b-table :data="inquiries" :loading="loading" striped hoverable>
        <b-table-column field="patient" label="Patient" v-slot="props">
          {{ patientName(props.row) || '—' }}
        </b-table-column>

        <b-table-column field="parent_id" label="Source Inquiry" v-slot="props">
          <router-link
            :to="{ name: 'AdminInquiryDetail', params: { id: props.row.parent_id } }"
          >
            {{ props.row.parent_id }}
          </router-link>
        </b-table-column>

        <b-table-column field="status" label="Result" v-slot="props">
          <span class="tag" :class="resultClass(props.row.status)">
            {{ props.row.status }}
          </span>
        </b-table-column>

        <b-table-column field="plan_status" label="Plan Status" v-slot="props">
          <span v-if="props.row.plan_status" class="tag" :class="planClass(props.row.plan_status)">
            {{ props.row.plan_status }}
          </span>
          <span v-else>-</span>
        </b-table-column>

        <b-table-column field="inquiry_id" label="New Inquiry" v-slot="props">
          <router-link
            v-if="props.row.inquiry_id"
            :to="{ name: 'AdminInquiryDetail', params: { id: props.row.inquiry_id } }"
          >
            {{ props.row.inquiry_id }}
          </router-link>
          <b-tooltip v-else-if="props.row.remote_error" :label="props.row.remote_error" position="is-left">
            <span class="has-text-danger">error</span>
          </b-tooltip>
          <span v-else>-</span>
        </b-table-column>

        <template #empty>
          <section class="section has-text-centered">
            <p>No inquiries in this batch.</p>
          </section>
        </template>
      </b-table>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

// Polling backs off along the Fibonacci sequence while idle; progress resets it (see BatchInquiryReport.vue).
const POLL_BASE_MS = 3000;
const POLL_MAX_MS = 30000;

export default {
  name: 'AdminBatchInquiryReport',
  components: { AdminLayout },
  data() {
    return {
      batch: null,
      loading: true,
      recovering: false,
      pollTimer: null,
      pollDelay: POLL_BASE_MS,
      prevPollDelay: 0,
    };
  },
  computed: {
    batchId() {
      return this.$route.params.id;
    },
    inquiries() {
      return (this.batch && this.batch.inquiries) || [];
    },
    ranCount() {
      return this.inquiries.filter((i) => i.status === 'complete').length;
    },
    failedCount() {
      return this.inquiries.filter((i) => i.status === 'failure').length;
    },
    inactiveCount() {
      return this.inquiries.filter((i) => i.plan_status === 'inactive').length;
    },
    unavailableCount() {
      return this.inquiries.filter((i) => i.plan_status === 'unavailable').length;
    },
    statusClass() {
      if (!this.batch) return 'is-light';
      return {
        'is-success': this.batch.status === 'complete',
        'is-info': this.batch.status === 'running',
        'is-light': this.batch.status === 'pending' || this.batch.status === 'canceled',
      };
    },
    // A batch can be closed only while it still has un-run inquiries (pending or running).
    canClose() {
      if (!this.batch || this.recovering) return false;
      return this.batch.status === 'pending' || this.batch.status === 'running';
    },
  },
  created() {
    this.fetchBatchInquiry();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    patientName(row) {
      return `${row.patient_first_name || ''} ${row.patient_last_name || ''}`.trim();
    },
    async exportBatchInquiry() {
      try {
        const response = await request.get(`/admin/batch-inquiries/${this.batchId}/export`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        let filename = `batch-${this.batchId}.csv`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match && match[1]) filename = match[1];
        }
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        this.$buefy.toast.open({ message: 'Failed to export the batch.', type: 'is-danger' });
      }
    },
    resultClass(status) {
      return {
        'is-success': status === 'complete',
        'is-danger': status === 'failure',
        'is-light': status === 'pending',
      };
    },
    planClass(status) {
      return {
        'is-success': status === 'active',
        'is-danger': status === 'inactive',
        'is-warning': status === 'unavailable',
        'is-light': status === 'draft',
      };
    },
    closeBatchInquiry() {
      this.$buefy.dialog.confirm({
        title: 'Close batch',
        message:
          'This cancels every inquiry in this batch that has not run yet. ' +
          'Inquiries that already ran are kept. This cannot be undone.',
        confirmText: 'Close batch',
        type: 'is-danger',
        onConfirm: async () => {
          this.recovering = true;
          try {
            const response = await request.post(`/admin/batch-inquiries/${this.batchId}/close`);
            this.batch = { ...this.batch, ...response.data.batch };
            this.stopPolling();
            this.$buefy.toast.open({ message: 'Batch closed.', type: 'is-success' });
          } catch (err) {
            const errors = (err.response && err.response.data && err.response.data.errors) || {};
            const message = errors.NOFIELD || Object.values(errors)[0] || 'Failed to close the batch.';
            this.$buefy.toast.open({ message, type: 'is-danger' });
          } finally {
            this.recovering = false;
          }
        },
      });
    },
    // fetchBatchInquiry loads the full batch (rows included); active batches poll the summary instead.
    async fetchBatchInquiry() {
      try {
        const response = await request.get(`/admin/batch-inquiries/${this.batchId}`);
        this.batch = response.data.batch;
        if (this.batch.status === 'pending' || this.batch.status === 'running') {
          this.schedulePoll(true);
        } else {
          this.stopPolling();
        }
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load the batch.', type: 'is-danger' });
        this.stopPolling();
      } finally {
        this.loading = false;
      }
    },
    // pollSummary fetches status/counts only (?summary=true) and merges them over the loaded batch.
    async pollSummary() {
      try {
        const response = await request.get(`/admin/batch-inquiries/${this.batchId}`, {
          params: { summary: true },
        });
        const summary = response.data.batch;
        const progressed = summary.completed !== this.batch.completed
          || summary.status !== this.batch.status;
        this.batch = { ...this.batch, ...summary };
        if (summary.status === 'pending' || summary.status === 'running') {
          this.schedulePoll(progressed);
        } else {
          // Finished: one last full fetch renders every row.
          this.stopPolling();
          this.fetchBatchInquiry();
        }
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load the batch.', type: 'is-danger' });
        this.stopPolling();
      }
    },
    schedulePoll(progressed) {
      if (progressed) {
        this.pollDelay = POLL_BASE_MS;
        this.prevPollDelay = 0;
      }
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        // Advance the Fibonacci backoff in case this poll finds no progress.
        const next = Math.min(this.pollDelay + this.prevPollDelay, POLL_MAX_MS);
        this.prevPollDelay = this.pollDelay;
        this.pollDelay = next;
        this.pollSummary();
      }, this.pollDelay);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
};
</script>
